/**
 * Shared CAP appointment scheduling UI (post-cap home check and install).
 */
import React, { useCallback, useState } from "react";
import styled from "@emotion/styled/macro";
import { Button } from "@sunrun/experience-ui-components";
import { DateTime } from "luxon";
import { useMediaQuery } from "react-responsive";
import { SiteSurveyAppointment } from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  AppointmentActionErrorModal,
  ConfirmRescheduleAppointmentModal,
  SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION,
  SiteSurveyAppointmentsSkeleton,
  SiteSurveyScheduledCard,
  SiteSurveySlotPicker,
  useSiteSurveySlotPickerState,
  type CapAppointmentSchedulerTestIds,
  type ConfirmationRefreshAction,
  type ScheduledStatusPill,
  type SiteSurveySlotPickerWarning,
} from "../../../checkout/components/molecules/siteSurveyScheduling";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

type CapAppointmentSchedulerProps = {
  testIds: CapAppointmentSchedulerTestIds;
  emptySlotsMessage: string;
  prospectTimeZoneId?: string;
  bookingSnapshotLoading: boolean;
  scheduledAppointment: SiteSurveyAppointment | undefined;
  slotBookingParentId: string | undefined;
  slots: SiteSurveySlot[] | undefined;
  slotsLoading: boolean;
  slotsError: unknown;
  retrySlots: () => void;
  onLoadSlots?: () => void;
  loadSlotsLabel?: string;
  bookingLoading: boolean;
  rescheduleLoading: boolean;
  bookSlot: (slot: SiteSurveySlot) => Promise<unknown>;
  rescheduleAppointment: () => Promise<unknown>;
  minSchedulableDate?: DateTime;
  pickerResetKey?: string | number;
  canSchedule?: boolean;
  scheduledStatusPill?: ScheduledStatusPill;
  scheduledCardMaxWidth?: string;
  showRescheduleButton?: boolean;
  confirmationRefresh?: ConfirmationRefreshAction;
  showStartTimeOnly?: boolean;
  scheduledArrivalIntro?: string;
  emptySlotsMode?: "inline" | "contactTeam";
  slotPickerWarning?: SiteSurveySlotPickerWarning;
};

const CAP_SCHEDULER_BORDER_RADIUS = "8px";

const CapAppointmentScheduler: React.FC<CapAppointmentSchedulerProps> = ({
  testIds,
  emptySlotsMessage,
  prospectTimeZoneId,
  bookingSnapshotLoading,
  scheduledAppointment,
  slotBookingParentId,
  slots,
  slotsLoading,
  slotsError,
  retrySlots,
  onLoadSlots,
  loadSlotsLabel,
  bookingLoading,
  rescheduleLoading,
  bookSlot,
  rescheduleAppointment,
  minSchedulableDate,
  pickerResetKey,
  canSchedule = true,
  scheduledStatusPill,
  scheduledCardMaxWidth,
  showRescheduleButton = true,
  confirmationRefresh,
  showStartTimeOnly = false,
  scheduledArrivalIntro,
  emptySlotsMode = "inline",
  slotPickerWarning,
}) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 930 });
  const COLUMNS = isMobile ? 1 : isTablet ? 2 : 3;

  const [showConfirmRescheduleModal, setShowConfirmRescheduleModal] =
    useState(false);
  const [showScheduleErrorModal, setShowScheduleErrorModal] = useState(false);
  const [showRescheduleErrorModal, setShowRescheduleErrorModal] =
    useState(false);

  const slotPickerSlots = slotBookingParentId ? slots : slots ?? [];
  const slotPickerLoading =
    !!slotBookingParentId && (slotsLoading || bookingSnapshotLoading);

  const {
    startDate,
    selectedSlot,
    setSelectedSlot,
    incrementDate,
    decrementDate,
  } = useSiteSurveySlotPickerState(
    slotPickerSlots,
    COLUMNS,
    minSchedulableDate,
    pickerResetKey,
    prospectTimeZoneId
  );

  const onSchedule = useCallback(() => {
    if (!selectedSlot) return;
    bookSlot(selectedSlot).catch(() => setShowScheduleErrorModal(true));
  }, [bookSlot, selectedSlot]);

  const onReschedule = useCallback(() => {
    setShowConfirmRescheduleModal(false);
    rescheduleAppointment().catch(() => setShowRescheduleErrorModal(true));
  }, [rescheduleAppointment]);

  const showInitialSkeleton =
    bookingSnapshotLoading && scheduledAppointment === undefined;

  if (showInitialSkeleton || (bookingLoading && !scheduledAppointment)) {
    return (
      <SiteSurveyAppointmentsSkeleton
        columns={COLUMNS}
        testId={testIds.loadingSkeleton}
        borderRadius={CAP_SCHEDULER_BORDER_RADIUS}
      />
    );
  }

  if (rescheduleLoading && scheduledAppointment) {
    return (
      <SiteSurveyAppointmentsSkeleton
        columns={COLUMNS}
        testId={testIds.rescheduleLoadingSkeleton}
        borderRadius={CAP_SCHEDULER_BORDER_RADIUS}
      />
    );
  }

  return (
    <SchedulerRoot data-testid={testIds.schedulerRoot}>
      {scheduledAppointment && (
        <SiteSurveyScheduledCard
          appointment={scheduledAppointment}
          timeZoneId={prospectTimeZoneId}
          maxWidth={scheduledCardMaxWidth}
          statusPill={scheduledStatusPill}
          onRequestReschedule={() => setShowConfirmRescheduleModal(true)}
          scheduledCardTestId={testIds.scheduledCard}
          rescheduleButtonTestId={testIds.rescheduleButton}
          showCancelButton={false}
          showRescheduleButton={showRescheduleButton}
          confirmationRefresh={confirmationRefresh}
          borderRadius={CAP_SCHEDULER_BORDER_RADIUS}
          showStartTimeOnly={showStartTimeOnly}
          scheduledArrivalIntro={scheduledArrivalIntro}
        />
      )}

      {!scheduledAppointment && (
        <>
          <SiteSurveySlotPicker
            slotListTestId={testIds.slotList}
            slotsRetryTestId={testIds.slotsRetry}
            noSlotsTestId={testIds.noSlots}
            columns={COLUMNS}
            timeZoneId={prospectTimeZoneId}
            startDate={startDate}
            incrementDate={incrementDate}
            decrementDate={decrementDate}
            slots={slotPickerSlots}
            slotsLoading={slotPickerLoading}
            slotsError={slotsError}
            onRetrySlots={retrySlots}
            selectedSlot={selectedSlot}
            onSelectSlot={setSelectedSlot}
            emptySlotsMode={emptySlotsMode}
            emptySlotsMessage={emptySlotsMessage}
            slotPickerWarning={slotPickerWarning}
            borderRadius={CAP_SCHEDULER_BORDER_RADIUS}
            showStartTimeOnly={showStartTimeOnly}
            onLoadSlots={onLoadSlots}
            loadSlotsLabel={loadSlotsLabel}
            loadSlotsTestId={testIds.loadSlots}
          />
          {!slotPickerWarning ? (
            <ActionsRow>
              {canSchedule ? (
                <Button
                  data-testid={testIds.scheduleSubmit}
                  disabled={!selectedSlot || bookingLoading}
                  onClick={onSchedule}
                >
                  Save
                </Button>
              ) : (
                <Button data-testid={testIds.signAgreementToSchedule} disabled>
                  Sign agreement to schedule
                </Button>
              )}
            </ActionsRow>
          ) : null}
        </>
      )}

      {showRescheduleButton ? (
        <ConfirmRescheduleAppointmentModal
          testId={testIds.confirmRescheduleModal}
          confirmRescheduleTestId={testIds.confirmRescheduleButton}
          open={showConfirmRescheduleModal}
          onConfirm={onReschedule}
          onClose={() => setShowConfirmRescheduleModal(false)}
        />
      ) : null}

      {showScheduleErrorModal && (
        <AppointmentActionErrorModal
          testId={testIds.scheduleErrorModal}
          title="Something went wrong creating your appointment"
          description={SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION}
          onRetry={() => {
            setShowScheduleErrorModal(false);
            onSchedule();
          }}
          onDismiss={() => setShowScheduleErrorModal(false)}
        />
      )}

      {showRescheduleButton && showRescheduleErrorModal && (
        <AppointmentActionErrorModal
          testId={testIds.rescheduleErrorModal}
          title="Something went wrong rescheduling your appointment"
          description={SCHEDULE_APPOINTMENT_ERROR_DESCRIPTION}
          onRetry={() => {
            setShowRescheduleErrorModal(false);
            onReschedule();
          }}
          onDismiss={() => setShowRescheduleErrorModal(false)}
        />
      )}
    </SchedulerRoot>
  );
};

const SchedulerRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export type { CapAppointmentSchedulerProps };
export { CapAppointmentScheduler };
